import { Point } from "geojson";
import { Identifier } from "react-admin";

import { DeviceType } from "./common";
import { LocationSource } from "./spaces";
import { ColumnsOf, DeviceBaseV2, Tracking } from "../data";
import { Maybe } from "../utility";

export const deviceState = {
  Securing: "securing",
  Found: "secured",
  Lost: "lost"
} as const;

export type DeviceStates = Array<(typeof deviceState)[keyof typeof deviceState]>;

export const featureTypes = {
  /**
   * If included in features array it means device is bi-directional
   * */
  Lamp: "lamp",
  /**
   * If included in features array it means device can send sensor data
   * */
  Sensors: "sensors",
  /**
   * If included in features array it means device is wifi access point
   * */
  WiFi: "wifi",
  /**
   * If included in features array it means device has gps capabilities
   * */
  Gps: "gps"
} as const;

export type FeatureType = (typeof featureTypes)[keyof typeof featureTypes];

export interface DeviceBase<TType extends string = DeviceType> {
  id: string;
  name: string;
  type: TType;
  tags?: Maybe<string[]>;

  tid?: Maybe<string>;
  gwid?: Maybe<string>;
  gw_name?: Maybe<string>;
  state?: Maybe<DeviceStates>;

  person?: Maybe<string>;

  created_by?: Maybe<string>;
  created_at: string;

  updated_by?: Maybe<string>;
  updated_at: string;

  is_present: boolean;

  present_on?: Maybe<string[]>;
  present_at?: Maybe<string>;

  location?: Maybe<Point>;
  located_at?: Maybe<string>;
  located_by?: Maybe<LocationSource>;
  address?: Maybe<string>;

  space_id: Identifier;
  space_name: string;

  site_id?: Maybe<Identifier>;
  site_name?: Maybe<string>;
  site_position?: Maybe<Point>;

  zone_id?: Maybe<Identifier>;
  zone_name?: Maybe<string>;

  wifi_name?: Maybe<string>;
  wifi_bssid?: Maybe<string>;
  wifi_ssid?: Maybe<string>;
  wifi_present_at?: Maybe<string>;

  best_gwid_by_effective_rssi?: Maybe<string>;
  gwid_effective_rssi?: Maybe<number>;
  best_notification_time_by_effective_rssi?: Maybe<string>;

  missing_at?: Maybe<string>;

  features: FeatureType[];

  tracking?: Maybe<Tracking>;

  meta?: Maybe<{
    secured: number;
    k?: Maybe<number>;
    deleted?: Maybe<boolean>;
    lowBatt?: Maybe<boolean>;
    firmware?: Maybe<string>;
    manufacturer?: Maybe<string>;
    modelNum?: Maybe<string>;
  }>;

  config?: DeviceBaseV2["config"];
  specification?: DeviceBaseV2["specification"];
  network?: DeviceBaseV2["network"];
  power?: DeviceBaseV2["power"];
}

export const DEVICE_BASE_COLUMNS: ColumnsOf<DeviceBase> = ["*"] as const;
